import {
  Button,
  Tabs,
  Space,
  message,
  Spin,
  Tag,
  Typography,
  Modal,
  Form,
  Input,
  Switch,
  Upload
} from 'antd';
import { useCallback, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import TabPane from 'antd/es/tabs/TabPane';
import Overview from './Pages/Overview';
import SupplyAndDemand from './Pages/SupplyAndDemand';
import TopGHGRisks from './Pages/TopGHGRisks';
import Methodologies from './Pages/Methodologies';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep, debounce } from 'lodash';
import {
  getCompassModuleById,
  updateCompassModule,
  updateCompassModuleWorkflow,
  uploadCompassUpdateImage
} from '../../api/compass_modules';

import { UploadOutlined } from '@ant-design/icons';
import { getDefaultRiskDriverContent, UpdateType } from './constants';
import { getCompassPublishPreview, type CompassPublishPreview } from '../../api/compass_modules';
import { ASSET_BASE_URL } from '../../api/axios';

export const defaultData = {
  overview: {
    description: null,
    ghgRatingText: '',
    sdgRatingText: ''
  },
  supplyAndDemand: {
    description: null,
    issuanceAndRetirementText: '',
    issuanceAndRetirementCitations: '',
    issuanceAndRetirementTakeawayText: '',
    returementPercentageText: '',
    returementPercentageCitations: '',
    returementPercentageTakeawayText: '',
    citations: null
  },
  topGHGRisks: {
    additionality: {
      impact: [{ id: uuidv4(), impact: 'low', description: '' }],
      title: '',
      description: [{ id: uuidv4(), type: 'content', data: null }],
      showSummary: true,
      citations: null,
      riskDrivers: [
        {
          id: uuidv4(),
          title: '',
          summary: null,
          data: [...getDefaultRiskDriverContent()]
        }
      ]
    },
    overCrediting: {
      impact: [{ id: uuidv4(), impact: 'low', description: '' }],
      title: '',
      description: [{ id: uuidv4(), type: 'content', data: null }],
      showSummary: true,
      citations: null,
      riskDrivers: [
        {
          id: uuidv4(),
          title: '',
          summary: null,
          data: [...getDefaultRiskDriverContent()]
        }
      ]
    },
    permanence: {
      impact: [{ id: uuidv4(), impact: 'low', description: '' }],
      title: '',
      description: [{ id: uuidv4(), type: 'content', data: null }],
      showSummary: true,
      citations: null,
      riskDrivers: [
        {
          id: uuidv4(),
          title: '',
          summary: null,
          data: [...getDefaultRiskDriverContent()]
        }
      ]
    },
    overLappingClaims: {
      impact: [{ id: uuidv4(), impact: 'low', description: '' }],
      description: [{ id: uuidv4(), type: 'content', data: null }],
      showSummary: true,
      citations: null,
      riskDrivers: [
        {
          id: uuidv4(),
          title: '',
          summary: null,
          data: [...getDefaultRiskDriverContent()]
        }
      ]
    }
  },
  methodologies: {
    hasScope: false,
    data: [{ id: uuidv4(), standard: '', version: '', methodology: '' }], // { standard: '', version: '', methodology: '' }[]
    overview: {
      scopeColumns: [{ id: uuidv4(), name: '', key: '' }], // { name: '', key: '' }[]
      citations: null,
      data: [
        {
          id: uuidv4(),
          methodology: '',
          standard: '',
          version: '',
          additionality: [{ id: uuidv4(), impact: 'low', description: '' }],
          overCrediting: [{ id: uuidv4(), impact: 'low', description: '' }],
          permanence: [{ id: uuidv4(), impact: 'low', description: '' }],
          overLappingClaims: [{ id: uuidv4(), impact: 'low', description: '' }],
          corsia: null,
          icvcm: null
        }
      ]
    },
    scope: {
      summary: '',
      ghgImpact: 'low',
      methodologyImpact: 'low',
      citations: null,
      ghgImpactDescription: '',
      methodologyImpactDescription: '',
      columns: [{ id: uuidv4(), name: '', key: '' }],
      data: [
        {
          id: uuidv4(),
          methodology: '',
          standard: '',
          version: '',
          risk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          transparencyRisk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          mitigations: {
            data: null
          }
        }
      ]
    },
    additionality: {
      summary: '',
      showGraph: true,
      ghgImpact: 'low',
      methodologyImpact: 'low',
      citations: null,
      graphTitle: '',
      graphTitleTooltipText: '',
      graphDescription: '',
      ghgImpactDescription: '',
      methodologyImpactDescription: '',
      data: [
        {
          id: uuidv4(),
          methodology: '',
          standard: '',
          version: '',
          approach: [
            {
              id: uuidv4(),
              type: 'none',
              data: null
            }
          ],
          risk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          transparencyRisk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          mitigations: {
            data: null
          }
        }
      ]
    },
    overCrediting: {
      summary: '',
      showGraph: true,
      ghgImpact: 'low',
      methodologyImpact: 'low',
      citations: null,
      graphTitle: '',
      graphTitleTooltipText: '',
      graphDescription: '',
      ghgImpactDescription: '',
      methodologyImpactDescription: '',
      data: [
        {
          id: uuidv4(),
          methodology: '',
          standard: '',
          version: '',
          approach: [
            {
              id: uuidv4(),
              type: 'none',
              data: null
            }
          ],
          risk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          transparencyRisk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          mitigations: {
            data: null
          }
        }
      ]
    },
    permanence: {
      summary: '',
      showGraph: true,
      ghgImpact: 'low',
      methodologyImpact: 'low',
      citations: null,
      graphTitle: '',
      graphTitleTooltipText: '',
      graphDescription: '',
      ghgImpactDescription: '',
      methodologyImpactDescription: '',
      data: [
        {
          id: uuidv4(),
          methodology: '',
          standard: '',
          version: '',
          approach: [
            {
              id: uuidv4(),
              type: 'none',
              data: null
            }
          ],
          risk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          transparencyRisk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          mitigations: {
            data: null
          }
        }
      ]
    },
    overLappingClaims: {
      summary: '',
      showGraph: true,
      ghgImpact: 'low',
      methodologyImpact: 'low',
      citations: null,
      graphTitle: '',
      graphTitleTooltipText: '',
      graphDescription: '',
      ghgImpactDescription: '',
      methodologyImpactDescription: '',
      data: [
        {
          id: uuidv4(),
          methodology: '',
          standard: '',
          version: '',
          approach: [
            {
              id: uuidv4(),
              type: 'none',
              data: null
            }
          ],
          risk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          transparencyRisk: {
            impact: [{ id: uuidv4(), impact: 'low', description: '' }],
            data: null
          },
          mitigations: {
            data: null
          }
        }
      ]
    },
    corsia: {
      citations: null,
      data: [
        {
          id: uuidv4(),
          standard: '',
          methodology: '',
          version: '',
          eligibility: null
        }
      ]
    }
  }
};

const ensureIdsInData = (data: any): any => {
  const normalized = cloneDeep(data);

  // Ensure IDs for topGHGRisks sections
  if (normalized.topGHGRisks) {
    Object.keys(normalized.topGHGRisks).forEach((riskType) => {
      const risk = normalized.topGHGRisks[riskType];

      // Ensure risk drivers have IDs
      if (risk.riskDrivers && Array.isArray(risk.riskDrivers)) {
        risk.riskDrivers = risk.riskDrivers.map((driver: any) => {
          if (!driver.id) {
            driver.id = uuidv4();
          }

          // Ensure data items within risk drivers have IDs
          if (driver.data && Array.isArray(driver.data)) {
            driver.data = driver.data.map((item: any) => {
              if (!item.id) {
                item.id = uuidv4();
              }
              return item;
            });
          }

          return driver;
        });
      }

      // Ensure impact items have IDs
      if (risk.impact && Array.isArray(risk.impact)) {
        risk.impact = risk.impact.map((item: any) => {
          if (!item.id) {
            item.id = uuidv4();
          }
          return item;
        });
      }
    });
  }

  // Ensure IDs for methodologies data
  if (normalized.methodologies) {
    Object.keys(normalized.methodologies).forEach((methodType) => {
      const methodology = normalized.methodologies[methodType];

      if (methodology.data && Array.isArray(methodology.data)) {
        methodology.data = methodology.data.map((item: any) => {
          if (!item.id) {
            item.id = uuidv4();
          }
          return item;
        });
      }

      if (methodology.scopeColumns && Array.isArray(methodology.scopeColumns)) {
        methodology.scopeColumns = methodology.scopeColumns.map((col: any) => {
          if (!col.id) {
            col.id = uuidv4();
          }
          return col;
        });
      }
    });
  }

  return normalized;
};

const Compass = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const queryClient = useQueryClient();
  const [data, setData] = useState(defaultData);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const [workflowState, setWorkflowState] = useState<'staging' | 'production'>('staging');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [showJsonViewer, setShowJsonViewer] = useState(false); // Hidden by default
  const [isPublishModalVisible, setIsPublishModalVisible] = useState(false);
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [updatesForm] = Form.useForm();
  const [previewData, setPreviewData] = useState({
    type: UpdateType.module_added,
    name: '',
    field_name: '',
    version: '',
    standard: '',
    image: ''
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isImageHover, setIsImageHover] = useState(false);
  const [isCheckingPreview, setIsCheckingPreview] = useState<boolean>(false);
  const [previewMethodologies, setPreviewMethodologies] = useState<
    | {
        name: string;
        version: string;
        standard: string;
      }[]
    | undefined
  >([]);
  const debounceSaveReq = useRef<any>(null);

  // Fetch compass module data
  const { isLoading, data: moduleData } = useQuery(
    ['compassModule', moduleId],
    () => getCompassModuleById(moduleId!),
    {
      enabled: !!moduleId,
      onSuccess: (fetchedData) => {
        // Use json_data if available, otherwise use defaultData
        const moduleJsonData = fetchedData?.json_data || defaultData;
        // Ensure all items have unique IDs
        const normalizedData = ensureIdsInData(moduleJsonData);
        setData(normalizedData);
        // Set workflow state from fetched data
        setWorkflowState(fetchedData?.workflow_state || 'staging');
        setIsDataInitialized(true);
      },
      onError: (err) => {
        console.error('Failed to fetch compass module:', err);
        message.error('Failed to load compass module data');
        // Fall back to default data on error
        const normalizedData = ensureIdsInData(defaultData);
        setData(normalizedData);
        setIsDataInitialized(true);
      }
    }
  );

  // Update mutation with optimistic update
  const {
    mutate: updateModuleData,
    mutateAsync: updateModuleDataAsync,
    isLoading: isUpdatingModuleData
  } = useMutation(
    (updatedData: any) => updateCompassModule(moduleId!, { json_data: updatedData }),
    {
      onMutate: async (newData) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['compassModule', moduleId]);

        // Snapshot the previous value
        const previousData = queryClient.getQueryData(['compassModule', moduleId]);

        // Optimistically update to the new value
        queryClient.setQueryData(['compassModule', moduleId], (old: any) => ({
          ...old,
          json_data: newData
        }));

        // Return a context with the previous and new data
        return { previousData };
      },
      onError: (_err, _newData, context: any) => {
        // If the mutation fails, use the context to roll back
        queryClient.setQueryData(['compassModule', moduleId], context.previousData);
        message.error('Failed to save changes');
      },
      onSuccess: () => {
        message.success('Changes saved successfully');
      },
      onSettled: () => {
        // Always refetch after error or success
        queryClient.invalidateQueries(['compassModule', moduleId]);
      }
    }
  );

  // Workflow state mutation
  const { mutate: updateWorkflow, isLoading: isUpdatingWorkflow } = useMutation(
    (payload: {
      state: 'staging' | 'production';
      send_email?: boolean;
      updates?: {
        type: 'module_added' | 'methodology_added';
        image?: string;
        name?: string;
        field_name?: string;
        version?: string;
        standard?: string;
      };
      newMethodologies?: Array<{
        name: string;
        version: string;
        standard: string;
      }>;
    }) => updateCompassModuleWorkflow(moduleId!, payload),
    {
      onSuccess: (data, payload) => {
        setWorkflowState(payload.state);
        message.success(`Moved to ${payload.state} successfully`);
        // Refetch to get the latest data
        queryClient.invalidateQueries(['compassModule', moduleId]);
      },
      onError: () => {
        message.error('Failed to update workflow state');
      }
    }
  );

  // Debounced save function
  const debouncedSave = useMemo(() => {
    debounceSaveReq.current = debounce((updatedData: any) => {
      if (moduleId && isDataInitialized && workflowState === 'staging') {
        return updateModuleDataAsync(updatedData);
      }
    }, 5000);
    return debounceSaveReq.current as any;
  }, [moduleId, updateModuleDataAsync, isDataInitialized, workflowState]);

  const handleWorkflowToggle = async () => {
    const flushed = debounceSaveReq.current?.flush?.();
    if (flushed && typeof (flushed as any).then === 'function') {
      try {
        await flushed;
      } catch {
        // ignore save error here; workflow mutation handles its own errors
      }
    }
    const movingTo = workflowState === 'staging' ? 'production' : 'staging';
    if (movingTo === 'production') {
      try {
        setIsCheckingPreview(true);
        const preview: CompassPublishPreview = await getCompassPublishPreview(moduleId!);
        setPreviewMethodologies(
          (preview?.diffMethodologies || []).map((m) => ({
            name: m.name,
            version: m.version,
            standard: m.standard
          }))
        );
        const hasNewMethodologies =
          Array.isArray(preview?.diffMethodologies) && preview.diffMethodologies.length > 0;
        const isFirstPublish = preview?.isModuleLive === false;

        if (isFirstPublish) {
          const defaultText = `New Project Type added: ${moduleData?.display_name}`;
          setPreviewData({
            type: UpdateType.module_added,
            name: defaultText,
            field_name: '',
            version: '',
            standard: '',
            image: ''
          });
          updatesForm.setFieldsValue({ name: defaultText, send_email: true });
          setSendEmail(true);
          setIsPublishModalVisible(true);
        } else if (hasNewMethodologies) {
          const first = preview.diffMethodologies[0];
          const hasMultiple = preview.diffMethodologies.length > 1;
          const defaultText = hasMultiple
            ? `New Methodologies added: ${preview.diffMethodologies
                .map((methodology) => methodology.name)
                .join(', ')}`
            : first?.preview_text;
          console.log({ defaultText });
          setPreviewData({
            type: UpdateType.methodology_added,
            name: defaultText,
            field_name: first?.name,
            version: first?.version,
            standard: first?.standard,
            image: ''
          });
          updatesForm.setFieldsValue({ name: defaultText, send_email: true });
          setSendEmail(true);
          setIsPublishModalVisible(true);
        } else {
          updateWorkflow({ state: 'production' });
        }
      } catch (e: any) {
        message.error(e.message || 'Failed to check publish preview');
      } finally {
        setIsCheckingPreview(false);
      }
    } else {
      updateWorkflow({ state: 'staging' });
    }
  };

  const submitPublish = async () => {
    try {
      if (!previewData.image) {
        await updatesForm.validateFields(['image']).catch(() => {});
        message.error('Please upload an image before moving to production');
        return;
      }
      const values = await updatesForm.validateFields();
      const finalName: string = values.name ?? previewData.name;
      updateWorkflow({
        state: 'production',
        send_email: !!values.send_email,
        updates: {
          type: previewData.type,
          name: finalName,
          image: previewData.image,
          field_name: ''
        },
        newMethodologies: previewMethodologies
      });
      setIsPublishModalVisible(false);
    } catch {
      // validation handled by antd
    }
  };

  const copyToClipboard = () => {
    const textArea = textAreaRef.current;
    if (textArea) {
      textArea.select();
      document.execCommand('copy');
      message.success('Copied to clipboard', 1);
    }
  };

  const updateData = useCallback(
    (path: string, newValue: any) => {
      const updateArray =
        (path === 'methodologies.hasScope' && (data?.methodologies?.scope?.data || []).length) === 0
          ? ['scope']
          : [
              'overview',
              'additionality',
              'overCrediting',
              'permanence',
              'overLappingClaims',
              'scope'
            ];
      if (
        path === 'methodologies.data' ||
        (path === 'methodologies.hasScope' && (data?.methodologies?.scope?.data || []).length) === 0
      ) {
        updateArray.forEach((tab) => {
          const updatedData = (
            path === 'methodologies.hasScope' ? data?.methodologies?.data || [] : newValue
          ).map((methodology, index) => {
            return {
              ...(cloneDeep(data.methodologies[tab]?.data[index]) ||
                (tab === 'overview'
                  ? {
                      id: uuidv4(),
                      additionality: [{ id: uuidv4(), impact: 'low', description: '' }],
                      overCrediting: [{ id: uuidv4(), impact: 'low', description: '' }],
                      permanence: [{ id: uuidv4(), impact: 'low', description: '' }],
                      overLappingClaims: [{ id: uuidv4(), impact: 'low', description: '' }],
                      corsia: null,
                      icvcm: null
                    }
                  : {
                      id: uuidv4(),
                      ...(tab === 'scope'
                        ? {}
                        : {
                            approach: [
                              {
                                id: uuidv4(),
                                type: 'cancel',
                                data: null
                              }
                            ]
                          }),
                      risk: {
                        impact: [{ id: uuidv4(), impact: 'low', description: '' }],
                        data: null
                      },
                      transparencyRisk: {
                        impact: 'low',
                        data: null
                      },
                      mitigations: {
                        data: null
                      }
                    })),
              methodology: methodology.methodology,
              standard: methodology.standard,
              version: methodology.version
            };
          });
          // Recursive call - handle directly to avoid infinite loop
          setData((prevState: any) => {
            const newState = cloneDeep(prevState);
            if (!newState.methodologies[tab]) {
              newState.methodologies[tab] = defaultData.methodologies[tab];
            }
            newState.methodologies[tab]['data'] = updatedData;
            // Trigger debounced save for this update too
            debouncedSave(newState);
            return newState;
          });
        });
      }

      setData((prevState: any) => {
        const newState = cloneDeep(prevState);

        const pathArray = path.split('.');
        let current = newState;

        for (let i = 0; i < pathArray.length - 1; i++) {
          if (!current[pathArray[i]]) {
            current[pathArray[i]] = {};
          }
          current = current[pathArray[i]];
        }

        current[pathArray[pathArray.length - 1]] = newValue;

        // Trigger debounced save
        debouncedSave(newState);

        return newState;
      });
    },
    [debouncedSave, data.methodologies]
  );

  // Show loading spinner while fetching data
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}>
        <Spin size="large" tip="Loading compass module..." />
      </div>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%', padding: '20px' }}>
      {/* Workflow State Controls */}
      <Typography.Title level={3}>{moduleData?.display_name}</Typography.Title>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
        <Space>
          <span>Current State:</span>
          <Tag color={workflowState === 'production' ? 'green' : 'blue'}>
            {workflowState === 'production' ? 'Production' : 'Staging'}
          </Tag>
        </Space>
        <Space>
          {isUpdatingModuleData && <Typography color="success">Saving data...</Typography>}
          <Button
            type="primary"
            onClick={handleWorkflowToggle}
            loading={isUpdatingWorkflow || isCheckingPreview || isUpdatingModuleData}>
            Move to {workflowState === 'staging' ? 'Production' : 'Staging'}
          </Button>
        </Space>
      </div>

      {/* Dev mode JSON viewer - hidden by default */}
      {showJsonViewer && (
        <>
          <Space align="end">
            <Button type="primary" onClick={copyToClipboard}>
              Copy to Clipboard
            </Button>
            <Button onClick={() => setShowJsonViewer(false)}>Hide JSON Viewer</Button>
          </Space>
          <textarea
            ref={textAreaRef}
            style={{ width: '100%', height: '300px' }}
            value={JSON.stringify(data, null, 2)}
            onChange={(e) => {
              updateModuleData(JSON.parse(e.target.value));
            }}
            // readOnly
          />
        </>
      )}

      <Tabs defaultActiveKey="1" type="card">
        <TabPane tab="Overview" key="1">
          <Overview
            data={data.overview}
            setData={updateData}
            readonly={workflowState === 'production'}
          />
        </TabPane>
        <TabPane tab="Supply and Demand" key="2">
          <SupplyAndDemand
            data={data.supplyAndDemand}
            setData={updateData}
            readonly={workflowState === 'production'}
          />
        </TabPane>
        <TabPane tab="Top GHG Risks" key="3">
          <Tabs defaultActiveKey="1" type="card">
            <TabPane tab="Additionality" key="1">
              <TopGHGRisks
                activeTab="additionality"
                data={data.topGHGRisks.additionality}
                setData={updateData}
                readonly={workflowState === 'production'}
              />
            </TabPane>
            <TabPane tab="Over-crediting" key="2">
              <TopGHGRisks
                activeTab="overCrediting"
                data={data.topGHGRisks.overCrediting}
                setData={updateData}
                readonly={workflowState === 'production'}
              />
            </TabPane>
            <TabPane tab="Permanence" key="4">
              <TopGHGRisks
                activeTab="permanence"
                data={data.topGHGRisks.permanence}
                setData={updateData}
                readonly={workflowState === 'production'}
              />
            </TabPane>
            <TabPane tab="Overlapping Claims" key="5">
              <TopGHGRisks
                activeTab="overLappingClaims"
                data={data.topGHGRisks.overLappingClaims}
                setData={updateData}
                readonly={workflowState === 'production'}
              />
            </TabPane>
          </Tabs>
        </TabPane>
        <TabPane tab="Methodologies" key="4">
          <Methodologies
            data={data.methodologies}
            setData={updateData}
            activeTab="overview"
            readonly={workflowState === 'production'}
          />
        </TabPane>
      </Tabs>
      <Modal
        title="Move to Production - Preview"
        open={isPublishModalVisible}
        onCancel={() => setIsPublishModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPublishModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={submitPublish} loading={isUpdatingWorkflow}>
            Move to production
          </Button>
        ]}
        width={600}>
        <Form
          form={updatesForm}
          layout="vertical"
          initialValues={{
            send_email: sendEmail,
            name: previewData.name,
            image: previewData.image
          }}>
          <Form.Item
            name="image"
            rules={[{ required: true, message: 'Please upload an image' }]}
            style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item label="Send email notifications" name="send_email" valuePropName="checked">
            <Switch onChange={(v) => setSendEmail(v)} defaultChecked />
          </Form.Item>
          <Form.Item label="Update image" required>
            {previewData.image ? (
              <div
                style={{ position: 'relative', width: '100%', maxWidth: 300 }}
                onMouseEnter={() => setIsImageHover(true)}
                onMouseLeave={() => setIsImageHover(false)}>
                <img
                  src={`${ASSET_BASE_URL}/${previewData.image}`}
                  alt="update"
                  style={{ width: '100%', maxWidth: 400, objectFit: 'contain', display: 'block' }}
                />
                {isUploadingImage && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(255,255,255,0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <Spin size="small" />
                  </div>
                )}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    right: 8,
                    display: isImageHover ? 'flex' : 'none',
                    gap: 8,
                    justifyContent: 'space-between'
                  }}>
                  <Upload
                    accept="image/*"
                    maxCount={1}
                    showUploadList={false}
                    customRequest={async ({ file, onSuccess, onError }) => {
                      try {
                        setIsUploadingImage(true);
                        const imageData = await uploadCompassUpdateImage(file as File);
                        console.log({ imageData });
                        const path = `${imageData.fileKey}`;
                        setPreviewData((prev) => ({ ...prev, image: path }));
                        updatesForm.setFieldsValue({ image: path });
                        message.success('Image updated');
                        if (onSuccess) onSuccess({ url: path } as any);
                      } catch (e: any) {
                        message.error(e?.message || 'Upload failed');
                        if (onError) onError(e);
                      } finally {
                        setIsUploadingImage(false);
                      }
                    }}>
                    <Button size="small" icon={<UploadOutlined />}>
                      Replace
                    </Button>
                  </Upload>
                  <Button
                    size="small"
                    danger
                    onClick={() => {
                      setPreviewData((prev) => ({ ...prev, image: '' }));
                      updatesForm.setFieldsValue({ image: undefined });
                      updatesForm.validateFields(['image']).catch(() => {});
                    }}>
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <Upload
                accept="image/*"
                maxCount={1}
                showUploadList={false}
                style={{ width: '100%' }}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    setIsUploadingImage(true);
                    const imageData = await uploadCompassUpdateImage(file as File);
                    const path = `${imageData.fileKey}`;
                    setPreviewData((prev) => ({ ...prev, image: path }));
                    updatesForm.setFieldsValue({ image: path });
                    message.success('Image uploaded');
                    if (onSuccess) onSuccess({ url: path } as any);
                  } catch (e: any) {
                    message.error(e?.message || 'Upload failed');
                    if (onError) onError(e);
                  } finally {
                    setIsUploadingImage(false);
                  }
                }}>
                <Button icon={<UploadOutlined />} loading={isUploadingImage}>
                  Upload image
                </Button>
              </Upload>
            )}
          </Form.Item>
          <Form.Item
            label="Update"
            name="name"
            rules={[{ required: true, message: 'Enter update text' }]}>
            <Input
              placeholder="e.g., New Project Type added: Biochar"
              onChange={(e) =>
                setPreviewData((prevData) => ({ ...prevData, name: e.target.value }))
              }
            />
          </Form.Item>
          <Typography.Text type="secondary">
            This update will be sent when moving to production.
          </Typography.Text>
        </Form>
      </Modal>
    </Space>
  );
};

export default Compass;
